import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { services, type Service } from "@/data/site-content";
import { serviceDetails } from "@/data/service-dropdown-content";
import { button3DClass } from "@/components/button-3d";

export const Route = createFileRoute("/services/$slug")({
  loader: ({ params }): Service => {
    const svc = services.find((s) => s.slug === params.slug);
    if (!svc) throw notFound();
    return svc;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Service not found" }, { name: "robots", content: "noindex" }],
      };
    }
    return {
      meta: [
        { title: loaderData.seoTitle ?? `${loaderData.title} | MG Content Labs` },
        { name: "description", content: loaderData.seoDescription ?? loaderData.summary },
        { property: "og:title", content: loaderData.seoTitle ?? loaderData.title },
        { property: "og:description", content: loaderData.seoDescription ?? loaderData.summary },
        { property: "og:url", content: `https://mgcontentlabs.com/services/${params.slug}` },
      ],
      links: [
        { rel: "canonical", href: `https://mgcontentlabs.com/services/${params.slug}` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: loaderData.title,
            description: loaderData.summary,
            provider: { "@type": "Organization", name: "MG Content Labs" },
          }),
        },
        ...(loaderData.faqs && loaderData.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: loaderData.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: ServiceDetail,
  notFoundComponent: NotFound,
});

function NotFound() {
  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="font-serif text-5xl">Service not found</h1>
        <p className="mt-4 text-brand-deep/60">Explore the full range of what we offer.</p>
          <Link to="/services" className={button3DClass("primary", "default", false, "mt-8")}>
            View all services
          </Link>
      </div>
      <SiteFooter />
    </div>
  );
}

function ServiceDetail() {
  const s = Route.useLoaderData();
  const related = services.filter((x) => x.slug !== s.slug).slice(0, 3);
  const detail = serviceDetails[s.slug];

  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />

      {/* Hero */}
      <header className="relative overflow-hidden bg-brand-muted py-24">
        <div className="pointer-events-none absolute inset-0 -z-0" aria-hidden="true">
          <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-brand-accent/20 blur-3xl" />
          <div className="absolute -right-32 top-40 h-[28rem] w-[28rem] rounded-full bg-[color:var(--color-brand-glow)]/15 blur-3xl" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-6">
          <Link to="/services" className="text-xs font-bold uppercase tracking-widest text-brand-accent hover:text-brand-deep">
            ← All services
          </Link>
          <div className="mt-6 text-[10px] font-bold uppercase tracking-widest text-brand-accent">
            {s.eyebrow}
          </div>
          <h1 className="mt-4 font-serif text-5xl leading-[1.05] md:text-6xl">{s.seoH1 ?? s.title}</h1>
          {s.seoIntro && (
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-brand-deep/80">
              {s.seoIntro}
            </p>
          )}
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-brand-deep/70">
            {s.overview ?? s.summary}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className={button3DClass("primary")}>
              Book a Free Audit
            </Link>
            <Link to="/pricing" className={button3DClass("secondary")}>
              See Plans
            </Link>
          </div>
        </div>
      </header>

      {/* Dropdown-style service snapshot */}
      {detail && (
        <section className="border-b border-brand-deep/10 bg-white py-20">
          <div className="mx-auto max-w-5xl px-6">
            <div className="bg-brand-muted p-8 md:p-10">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">At a glance</div>
              <h2 className="mt-2 font-serif text-3xl">{detail.label}</h2>
              <p className="mt-3 max-w-3xl text-base leading-relaxed text-brand-deep/70">{detail.whatIs}</p>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Why it matters</div>
                  <p className="mt-1 text-sm leading-relaxed text-brand-deep/70">{detail.whyItMatters}</p>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">How you benefit</div>
                  <p className="mt-1 text-sm leading-relaxed text-brand-deep/70">{detail.howYouBenefit}</p>
                </div>
              </div>
              <div className="mt-6">
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Key Offerings</div>
                <ul className="mt-2 grid gap-2 text-sm text-brand-deep/70 sm:grid-cols-2">
                  {detail.offerings.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 text-brand-accent">→</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {detail.whatToExpect && detail.whatToExpect.length > 0 && (
                <div className="mt-6">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">What to Expect</div>
                  <ul className="mt-2 grid gap-2 text-sm text-brand-deep/70 sm:grid-cols-2">
                    {detail.whatToExpect.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1 text-brand-accent">→</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-6">
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">How Customers Benefit</div>
                <p className="mt-1 text-sm leading-relaxed text-brand-deep/70">{detail.howCustomersBenefit}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* What you get */}
      <section className="py-24">
        <div className="mx-auto max-w-5xl px-6">
          {/* What is this */}
          {s.whatIs && (
            <div className="mb-20">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                What it is
              </div>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl">
                What is <span className="italic text-brand-accent">{s.title}</span>?
              </h2>
              <div className="mt-3 h-1 w-16 bg-brand-accent" />
              <p className="mt-8 max-w-3xl text-lg leading-relaxed text-brand-deep/75">
                {s.whatIs}
              </p>
            </div>
          )}

          {/* How it helps */}
          {s.howItHelps && s.howItHelps.length > 0 && (
            <div className="mb-20">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                Why it matters
              </div>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl">How it helps your business</h2>
              <div className="mt-3 h-1 w-16 bg-brand-accent" />
              <div className="mt-12 grid gap-x-10 gap-y-10 md:grid-cols-2">
                {s.howItHelps.map((h: { title: string; description: string }, i: number) => (
                  <div key={h.title} className="flex gap-5">
                    <div className="font-serif text-3xl italic text-brand-accent leading-none">
                      0{i + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">{h.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-brand-deep/70">{h.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What to expect */}
          {s.whatToExpect && s.whatToExpect.length > 0 && (
            <div className="mb-20 border-t border-brand-deep/10 pt-16">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                The journey
              </div>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl">What to expect</h2>
              <div className="mt-3 h-1 w-16 bg-brand-accent" />
              <div className="mt-12 space-y-6">
                {s.whatToExpect.map((w: { title: string; description: string }) => (
                  <div key={w.title} className="grid gap-4 border-l-2 border-brand-accent/40 pl-6 md:grid-cols-[220px_1fr] md:gap-8">
                    <div className="text-sm font-bold uppercase tracking-wider text-brand-deep">
                      {w.title}
                    </div>
                    <p className="text-brand-deep/70">{w.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ideal for */}
          {s.idealFor && s.idealFor.length > 0 && (
            <div className="mb-20 bg-brand-muted p-10">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                Best fit
              </div>
              <h2 className="mt-3 font-serif text-2xl md:text-3xl">Ideal for</h2>
              <ul className="mt-6 space-y-3">
                {s.idealFor.map((f: string) => (
                  <li key={f} className="flex items-start gap-3 text-brand-deep/80">
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand-accent" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <h2 className="font-serif text-3xl md:text-4xl">What's inside this engagement</h2>
          <div className="mt-3 h-1 w-16 bg-brand-accent" />

          {s.deliverables && s.deliverables.length > 0 ? (
            <div className="mt-12 grid gap-x-10 gap-y-12 md:grid-cols-2">
              {s.deliverables.map((d: { title: string; description: string }, i: number) => (
                <div key={d.title} className="border-t border-brand-deep/10 pt-6">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                    Deliverable {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-2 text-xl font-bold">{d.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-deep/70">{d.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <ul className="mt-10 grid gap-3 text-brand-deep/70 sm:grid-cols-2">
              {s.bullets.map((b: string) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-1 text-brand-accent">→</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Process */}
      {s.process && s.process.length > 0 && (
        <section className="bg-brand-muted py-24">
          <div className="mx-auto max-w-5xl px-6">
            <h2 className="font-serif text-3xl md:text-4xl">How we run it</h2>
            <div className="mt-3 h-1 w-16 bg-brand-accent" />
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {s.process.map((p: { step: string; title: string; description: string }) => (
                <div key={p.step} className="border-t border-brand-deep pt-4">
                  <div className="font-serif text-3xl italic text-brand-accent">{p.step}</div>
                  <h3 className="mt-2 text-base font-bold">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-deep/70">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Outcomes */}
      {s.outcomes && s.outcomes.length > 0 && (
        <section className="bg-brand-deep py-20 text-white">
          <div className="mx-auto max-w-5xl px-6">
            <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
              Outcomes clients see
            </div>
            <div className="mt-8 grid gap-10 md:grid-cols-3">
              {s.outcomes.map((o: { metric: string; label: string }) => (
                <div key={o.label} className="border-t border-white/15 pt-6">
                  <div className="font-serif text-5xl italic text-brand-accent">{o.metric}</div>
                  <div className="mt-3 text-sm text-white/70">{o.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {s.faqs && s.faqs.length > 0 && (
        <section className="py-24">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="font-serif text-3xl md:text-4xl">Frequently asked</h2>
            <div className="mt-3 h-1 w-16 bg-brand-accent" />
            <div className="mt-10 space-y-8">
              {s.faqs.map((f: { q: string; a: string }) => (
                <div key={f.q} className="border-t border-brand-deep/10 pt-6">
                  <h3 className="text-lg font-bold">{f.q}</h3>
                  <p className="mt-3 text-brand-deep/70">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related services */}
      <section className="border-t border-brand-deep/10 py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-10 flex items-end justify-between">
            <h2 className="font-serif text-2xl">Explore more services</h2>
            <Link to="/services" className="text-sm font-semibold text-brand-accent hover:text-brand-deep">
              All services →
            </Link>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to="/services/$slug"
                params={{ slug: r.slug }}
                className="group block border-t border-brand-deep/10 pt-6"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                  {r.eyebrow}
                </div>
                <h3 className="mt-2 text-lg font-bold transition-colors group-hover:text-brand-accent">
                  {r.title}
                </h3>
                <p className="mt-3 text-sm text-brand-deep/60">{r.summary}</p>
                <span className="mt-4 inline-block text-xs font-semibold text-brand-accent">
                  Know more →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-muted py-20">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl">Want this shipped for your brand?</h2>
          <p className="max-w-xl text-brand-deep/70">
            Book a 30-minute strategy call. We'll map exactly how {s.title} would look inside your
            content engine.
          </p>
          <Link to="/contact" className={button3DClass("primary")}>
            Book a Strategy Call
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}